/**
 * Custom word list validation.
 *
 * Per IMPLEMENTATION_PLAN §5.10 (security depends on this):
 *   - Lowercased + trimmed at parse.
 *   - Reject lines containing any character in Unicode category Cc/Cf/Co/Cn/So
 *     (control, format, private-use, unassigned, symbol-other).
 *   - Allow standard letters + digits + space + hyphen + apostrophe.
 *   - Max word length: 40 graphemes (via Intl.Segmenter).
 *   - Reject emoji (covered by So category rejection).
 *   - Optional `:tier` suffix; untagged → medium.
 *   - Invalid lines are flagged with an error reason.
 *
 * Unicode category detection: We use a practical approach, allow only
 * characters that are explicitly permitted (allowlist), rather than trying
 * to enumerate all rejected categories. The allowed set:
 *   \p{L}: Unicode letters (covers Latin, Cyrillic, Greek, CJK, etc.)
 *   \p{N}: Unicode numbers
 *   space, hyphen, apostrophe
 *
 * This naturally rejects Cc/Cf/Co/Cn/So (control, format, private-use,
 * unassigned, other-symbol) and emoji (which are in So or Sm/Sk/Sc with
 * modifier sequences). Per §5.10 lock: "allow \p{L}\p{N} space hyphen apostrophe".
 */

import { normalize } from "./normalize.js";
import type { CustomWord, WordTier } from "./types.js";

/** Characters explicitly allowed in custom words. */
const ALLOWED_CHARS = /^[\p{L}\p{N} '-]+$/u;

/**
 * Max grapheme count per word (Intl.Segmenter). Exported so UI copy that
 * states the limit can't drift from the validator (review 2026-06-11 found
 * the lobby telling users "25 max" against this 40).
 */
export const MAX_GRAPHEMES = 40;

/** Tier suffix pattern: "word:easy", "word:medium", "word:hard" */
const TIER_SUFFIX = /^(.+):([a-z]+)$/;

const VALID_TIERS = new Set<string>(["easy", "medium", "hard"]);

export type ValidatedLine =
  | { valid: true; word: string; tier: WordTier }
  | { valid: false; original: string; reason: string };

/**
 * Count graphemes using Intl.Segmenter.
 * Falls back to `.length` if Intl.Segmenter is unavailable (old environments).
 */
function countGraphemes(str: string): number {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const segmenter = new Intl.Segmenter();
    let count = 0;
    for (const _ of segmenter.segment(str)) {
      count++;
    }
    return count;
  }
  // Fallback: code point count (less accurate but safe)
  return [...str].length;
}

/** Combining diacritic range (U+0300..U+036F), stripped after NFD decompose. */
const COMBINING_DIACRITICS = /[̀-ͯ]/g;

/**
 * Validate a single line from a custom word list.
 *
 * Returns `{ valid: true, word, tier }` on success, or
 * `{ valid: false, original, reason }` on failure.
 *
 * **NFD normalization** (M3.fix B5, §5.10): per §13.5 the canonical pipeline
 * is NFD-decompose then strip combining marks. "café" stored as NFC ("café"
 * = c+a+f+é) decomposes to NFD ("café" = c+a+f+e+◌́) and the diacritic is
 * dropped, leaving "cafe". This matches the player-guess normalizer
 * (shared/src/normalize.ts) so M4's guess matching can compare apples to
 * apples regardless of which form the word was stored in.
 */
export function validateLine(line: string): ValidatedLine {
  const original = line;

  // 1. Trim + NFD-decompose + strip combining marks + lowercase
  let processed = line.normalize("NFD").replace(COMBINING_DIACRITICS, "").trim().toLowerCase();

  if (processed.length === 0) {
    return { valid: false, original, reason: "empty-line" };
  }

  // 2. Parse optional :tier suffix
  let tier: WordTier = "medium";
  const tierMatch = TIER_SUFFIX.exec(processed);
  if (tierMatch) {
    const maybeTier = tierMatch[2]!;
    if (!VALID_TIERS.has(maybeTier)) {
      return { valid: false, original, reason: `invalid-tier:${maybeTier}` };
    }
    tier = maybeTier as WordTier;
    processed = tierMatch[1]!.trim();
  }

  // 3. Check grapheme count
  const graphemeCount = countGraphemes(processed);
  if (graphemeCount > MAX_GRAPHEMES) {
    return { valid: false, original, reason: `too-long:${graphemeCount}-graphemes` };
  }
  if (graphemeCount === 0) {
    return { valid: false, original, reason: "empty-after-trim" };
  }

  // 4. Allowlist check: rejects Cc/Cf/Co/Cn/So and emoji
  if (!ALLOWED_CHARS.test(processed)) {
    return { valid: false, original, reason: "invalid-characters" };
  }

  return { valid: true, word: processed, tier };
}

/**
 * Parse and validate a full custom word list text.
 *
 * Splits on newlines, validates each non-empty line, returns:
 *   - `validWords`: array of CustomWord ready to store
 *   - `invalidLines`: array of validation failures for UI highlighting
 */
export function validateCustomWords(text: string): {
  validWords: CustomWord[];
  invalidLines: Array<{ lineIndex: number; original: string; reason: string }>;
} {
  const lines = text.split("\n");
  const seen = new Set<string>();
  const validWords: CustomWord[] = [];
  const invalidLines: Array<{ lineIndex: number; original: string; reason: string }> = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.trim().length === 0) continue; // skip blank lines

    const result = validateLine(line);
    if (result.valid) {
      const key = normalize(result.word);
      if (!key || seen.has(key)) {
        invalidLines.push({ lineIndex: i, original: line, reason: key ? "duplicate-word" : "empty-after-normalization" });
      } else {
        seen.add(key);
        validWords.push({ word: result.word, tier: result.tier });
      }
    } else {
      invalidLines.push({ lineIndex: i, original: result.original, reason: result.reason });
    }
  }

  return { validWords, invalidLines };
}
